/**
 * Component for our Analytics section displaying customer-related Analytics data.
 *
 * @component
 */

import {
  Card,
  Col,
  Divider,
  message,
  Row,
  Select,
  Spin,
  Tooltip,
  Typography,
  Button,
  Menu,
  Dropdown,
} from 'antd';
import axios from 'axios';
import React from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import { IconTray } from '../../../../components/CustomReturnsIcons';
import Upgrade from '../../../../components/Upgrade';
import { SubscriptionContext } from '../../../../contexts/SubscriptionContext';
import { CSVLink } from 'react-csv';
import { Parser } from 'json2csv';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const { Option } = Select;

const { Text } = Typography;

const exportHeaders = [
  {
    label: 'Return Rate',
    value: 'returnRate',
    key: 'returnRate',
  },
  {
    label: 'Return Volume',
    value: 'numberOfTotalReturns',
    key: 'numberOfTotalReturns',
  },
  {
    label: 'Order Volume',
    value: 'numberOfOrders',
    key: 'numberOfOrders',
  },
  {
    label: 'Return Value',
    value: 'totalReturnValue',
    key: 'totalReturnValue',
  },
  {
    label: 'Average Return Value',
    value: 'averageReturnValue',
    key: 'averageReturnValue',
  },
  {
    label: 'Completed Return Value',
    value: 'valueOfCompletedReturns',
    key: 'valueOfCompletedReturns',
  },
  {
    label: 'Returned Items',
    value: 'numberOfTotalProducts',
    key: 'numberOfTotalProducts',
  },
  {
    label: 'Average items per return',
    value: 'averageProductsPerReturn',
    key: 'averageProductsPerReturn',
  },
  {
    label: 'Completed Return Volume',
    value: 'numberOfCompletedReturns',
    key: 'numberOfCompletedReturns',
  },
  {
    label: 'Currency',
    value: 'currency',
    key: 'currency',
  },
  {
    label: 'Refund Rate',
    value: 'rateOfRefundToOriginal',
    key: 'rateOfRefundToOriginal',
  },
  {
    label: 'Refund Volume',
    value: 'numberOfRefundToOriginal',
    key: 'numberOfRefundToOriginal',
  },
  {
    label: 'Refund Value',
    value: 'valueOfRefundToOriginal',
    key: 'valueOfRefundToOriginal',
  },
  {
    label: 'Exchange Rate',
    value: 'rateOfRefundToExchange',
    key: 'rateOfRefundToExchange',
  },
  {
    label: 'Exchange Volume',
    value: 'numberOfRefundToExchange',
    key: 'numberOfRefundToExchange',
  },
  {
    label: 'Exchange Value',
    value: 'valueOfRefundToExchange',
    key: 'valueOfRefundToExchange',
  },
  {
    label: 'Store Credit Rate',
    value: 'rateOfRefundToCredit',
    key: 'rateOfRefundToCredit',
  },
  {
    label: 'Store Credit Volume',
    value: 'numberOfRefundToCredit',
    key: 'numberOfRefundToCredit',
  },
  {
    label: 'Store Credit Value',
    value: 'valueOfRefundToCredit',
    key: 'valueOfRefundToCredit',
  },
  {
    label: 'Shipping Name/Email Address',
    value: 'shippingNameEmailAddress',
    key: 'shippingNameEmailAddress',
  },
];

class Stats extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      numberOfTotalReturns: '-',
      numberOfTotalProducts: '-',
      averageProductsPerReturn: '-',
      totalReturnValue: '-',
      averageReturnValue: '-',
      numberOfOrders: '-',
      returnRate: '-',
      currency: '-',
      numberOfCompletedReturns: '-',
      valueOfCompletedReturns: '-',
      numberOfRefundToOriginal: '-',
      valueOfRefundToOriginal: '-',
      rateOfRefundToOriginal: '-',
      numberOfRefundToExchange: '-',
      valueOfRefundToExchange: '-',
      rateOfRefundToExchange: '-',
      numberOfRefundToCredit: '-',
      valueOfRefundToCredit: '-',
      rateOfRefundToCredit: '-',
      dataCSVToBeExported: [],
      exportLoading: false,
      pdfLoading: false,
      timestamp: Date.now(),
    };
    this.printRef = React.createRef();
    this.csvLinkRef = React.createRef();
  }

  static contextType = SubscriptionContext;

  handleExportPdf = async () => {
    this.setState({ pdfLoading: true });

    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: 'a4',
      });

      const pageWidth = pdf.internal.pageSize.width;
      pdf.setFontSize(16);
      const title = 'Customer Analytics Statistics';
      const titleWidth = pdf.getStringUnitWidth(title) * pdf.internal.getFontSize() / pdf.internal.scaleFactor;
      pdf.text(title, (pageWidth - titleWidth) / 2, 40);
      
      pdf.setFontSize(12);
      const dateText = `Generated on: ${new Date().toLocaleDateString()}`;
      const dateWidth = pdf.getStringUnitWidth(dateText) * pdf.internal.getFontSize() / pdf.internal.scaleFactor;
      pdf.text(dateText, (pageWidth - dateWidth) / 2, 60);

      const statsData = [
        ['Return Rate', this.state.returnRate],
        ['Return Volume', this.state.numberOfTotalReturns],
        ['Order Volume', this.state.numberOfOrders],
        ['Return Value', `${this.state.totalReturnValue} ${this.state.currency}`],
        ['Average Return Value', `${this.state.averageReturnValue} ${this.state.currency}`],
        ['Completed Return Value', `${this.state.valueOfCompletedReturns} ${this.state.currency}`],
        ['Returned Items', this.state.numberOfTotalProducts],
        ['Average items per return', this.state.averageProductsPerReturn],
        ['Completed Return Volume', this.state.numberOfCompletedReturns],
        ['Refund Rate', this.state.rateOfRefundToOriginal],
        ['Refund Volume', this.state.numberOfRefundToOriginal],
        ['Refund Value', `${this.state.valueOfRefundToOriginal} ${this.state.currency}`],
        ['Exchange Rate', this.state.rateOfRefundToExchange],
        ['Exchange Volume', this.state.numberOfRefundToExchange],
        ['Exchange Value', `${this.state.valueOfRefundToExchange} ${this.state.currency}`],
        ['Store Credit Rate', this.state.rateOfRefundToCredit],
        ['Store Credit Volume', this.state.numberOfRefundToCredit],
        ['Store Credit Value', `${this.state.valueOfRefundToCredit} ${this.state.currency}`],
      ];

      const tableWidth = 400; 
      const leftMargin = (pageWidth - tableWidth) / 2;

      autoTable(pdf, {
        head: [['Metric', 'Value']],
        body: statsData,
        startY: 80,
        margin: { 
          top: 80, 
          left: leftMargin, 
          right: leftMargin 
        },
        tableWidth: tableWidth,
        styles: {
          fontSize: 10,
          cellPadding: { top: 8, right: 5, bottom: 8, left: 5 }, 
          minCellHeight: 25, 
        },
        headStyles: {
          fillColor: [66, 139, 202],
          textColor: 255,
          fontStyle: 'bold',
          halign: 'left',
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
        theme: 'striped',
        columnStyles: {
          0: { cellWidth: 200, fontStyle: 'bold', halign: 'left' }, 
          1: { cellWidth: 200, halign: 'left' }, 
        },
      });

      pdf.save(`RichReturns-Statistics-${this.state.timestamp}.pdf`);
      message.success('PDF downloaded successfully');
    } catch (error) {
      console.error('PDF export error:', error);
      this.setState({ pdfLoading: false });
      return message.error('Failed to generate PDF');
    } finally {
      this.setState({ pdfLoading: false });
    }
  };
  async handleExportCsv() {
    this.setState({
      exportLoading: true,
    });

    try {
      const { loading, dataCSVToBeExported, exportLoading, ...keepFields } =
        this.state;
      const dataParser = new Parser({
        fields: exportHeaders,
        quote: '',
      });

      const data = dataParser.parse([keepFields]);

      this.setState({
        exportLoading: false,
        dataCSVToBeExported: data,
      });

      setTimeout(() => {
        if (
          this.csvLinkRef.current &&
          this.state.dataCSVToBeExported.length > 0
        ) {
          this.csvLinkRef.current.link.click();
          message.success('CSV downloaded successfully!', 2);
          this.handleResetDataToBeExported();
        }
      }, 1000);
    } catch (err) {
      this.setState({
        exportLoading: false,
      });
      return message.error('Error creating report csv.', err);
    }
  }

  handleResetDataToBeExported = () => {
    this.setState({
      dataCSVToBeExported: [],
    });
  };

  async componentDidMount() {
    const url = window.location.href;
    const urlObject = new URL(url);
    const customerId = urlObject.searchParams.get('customerId');
    if (this.props.customerId || customerId) {
      try {
        const response = await axios.get(
          `/api/analytics/customers/${
            customerId ? customerId : this.props.customerId
          }`
        );

        if (customerId) {
          const { shippingNameEmailAddress } = response.data;
          this.props.updateCustomerDetails(
            customerId,
            shippingNameEmailAddress
          );
        }
        return this.setState({
          ...response.data,
          loading: false,
        });
      } catch (err) {
        return message.error(
          'something went wrong in getting the customer details',
          5
        );
      }
    }
  }

  render() {
    const onChange = async (value) => {
      this.setState({
        loading: true,
      });
      try {
        const response = await axios.get(`/api/analytics/customers/${value}`);
        this.props.updateCustomerDetails(value);
        return this.setState({
          ...response.data,
          loading: false,
        });
      } catch (err) {
        this.setState({
          loading: false,
        });
        return message.error(
          'something went wrong in getting the customer details',
          5
        );
      }
    };

    if (
      this.state.loading ||
      this.context.featureAnalyticsAdvanced === undefined
    ) {
      return null;
    }
    return (
      <React.Fragment>
        {this.context.featureAnalyticsAdvanced ? (
          <React.Fragment>
            <h1 className="TextHeading TextHeading--sm u-marginBottom--md">
              Customers
            </h1>

            {/* <Row type="flex" justify="start" align="top">
              <Col>
                <Tooltip
                  placement="top"
                  title="Shows Returns-Analytics for a specific customer."
                >
                  <Icon style={{ paddingTop: "13px" }} type="info-circle" />
                </Tooltip>
              </Col>
              <Col>
                &nbsp;
                <span className="TextHeading TextHeading--sm">Customer Analytics</span>
              </Col>
            </Row> */}

            <Row
              type="flex"
              justify="space-between"
              align="middle"
              style={{ rowGap: '15px' }}
              gutter={16}
            >
              <Col
                xs={{ span: 24 }}
                sm={{ span: 12 }}
                md={{ span: 12 }}
                lg={{ span: 16 }}
              >
                <Text style={{ fontSize: 14 }}>
                  Search for a customer by entering their first name, last name
                  or email address to view insights.
                </Text>
              </Col>
              <Col
                xs={{ span: 24 }}
                sm={{ span: 6 }}
                md={{ span: 6 }}
                lg={{ span: 6 }}
              >
                {this.state.dataCSVToBeExported.length > 0 && (
                  <CSVLink
                    data={this.state.dataCSVToBeExported}
                    filename={`RichReturns-Statics-${this.state.timestamp}.csv`}
                    className="hidden"
                    ref={this.csvLinkRef}
                  />
                )}

                {this.props.customerNameEmail && (
                  <Dropdown
                    overlay={
                      <Menu>
                        <Menu.Item
                          key="csv"
                          onClick={this.handleExportCsv.bind(this)}
                          disabled={this.state.exportLoading}
                        >
                          Export CSV
                        </Menu.Item>
                        <Menu.Item
                          key="pdf"
                          onClick={this.handleExportPdf}
                          disabled={this.state.pdfLoading}
                        >
                          Export PDF
                        </Menu.Item>
                      </Menu>
                    }
                    placement="bottomRight"
                  >
                    <Button block={true}>Export</Button>
                  </Dropdown>
                )}
              </Col>
              <Col
                xs={{ span: 24 }}
                sm={{ span: 12 }}
                md={{ span: 12 }}
                lg={{ span: 8 }}
              >
                <Tooltip
                  placement="top"
                  title="Search for a customer. Only customers with at least one Return in Rich Returns will show up as a result."
                >
                  {/* <Icon type="info-circle" /> */}
                  <Select
                    data-intercom-target="customer-search-analytics-customers"
                    style={{ width: '100%' }}
                    showSearch
                    value={this.props.customerNameEmail}
                    placeholder="Search for a customer"
                    onChange={onChange}
                    onSearch={(value) => this.props.onSearch(value)}
                    filterOption={false}
                    notFoundContent={
                      <div className="flex-row flex-middle-xxs flex-center-xxs">
                        <div className="flex-col-xxs-12">
                          <IconTray size="md" />
                        </div>
                        <div>
                          <p className="TextBody TextBody--xxxs">No Data</p>
                        </div>
                      </div>
                    }
                  >
                    {this.props.matchingCustomers &&
                    this.props.matchingCustomers.length > 0
                      ? this.props.matchingCustomers.map((value) => {
                          return (
                            <Option value={value.customerId}>
                              {`${value.shippingName} (${value.customerEmail})`}
                            </Option>
                          );
                        })
                      : null}
                  </Select>
                </Tooltip>
              </Col>
            </Row>
            <div ref={this.printRef}>
              <Spin
                spinning={this.state.loading}
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
              >
                <Row
                  type="flex"
                  gutter={10}
                  style={{
                    marginTop: '20px',
                    textAlign: 'center',
                    rowGap: '15px',
                  }}
                >
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 12 }}
                    md={{ span: 12 }}
                    lg={{ span: 8 }}
                  >
                    <Tooltip
                      placement="top"
                      title="Number of orders with a subsequent return divided by the number of orders."
                    >
                      <Card
                        title="Return Rate"
                        data-intercom-target="return-rate-analytics-customers"
                        bordered={true}
                        hoverable={false}
                        extra={<React.Fragment></React.Fragment>}
                      >
                        {this.state.returnRate}
                      </Card>
                    </Tooltip>
                  </Col>
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 12 }}
                    md={{ span: 12 }}
                    lg={{ span: 8 }}
                  >
                    <Tooltip placement="top" title="Number of Returns created.">
                      <Card
                        title="Return Volume"
                        data-intercom-target="return-volume-analytics-customers"
                        bordered={true}
                        hoverable={false}
                        extra={<React.Fragment></React.Fragment>}
                      >
                        {this.state.numberOfTotalReturns}
                      </Card>
                    </Tooltip>
                  </Col>
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 12 }}
                    md={{ span: 12 }}
                    lg={{ span: 8 }}
                  >
                    <Tooltip placement="top" title="Number of Orders received.">
                      <Card
                        title="Order Volume"
                        data-intercom-target="order-volume-analytics-customers"
                        bordered={true}
                        hoverable={false}
                        extra={<React.Fragment></React.Fragment>}
                      >
                        {this.state.numberOfOrders}
                      </Card>
                    </Tooltip>
                  </Col>

                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 12 }}
                    md={{ span: 12 }}
                    lg={{ span: 8 }}
                  >
                    <Tooltip placement="top" title="Value of returned items.">
                      <Card
                        title="Return Value"
                        data-intercom-target="return-value-analytics-customers"
                        bordered={true}
                        hoverable={false}
                        extra={<React.Fragment></React.Fragment>}
                      >
                        {this.state.totalReturnValue} {this.state.currency}
                      </Card>
                    </Tooltip>
                  </Col>
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 12 }}
                    md={{ span: 12 }}
                    lg={{ span: 8 }}
                  >
                    <Tooltip
                      placement="top"
                      title="Value of returned items divided by the number of returns."
                    >
                      <Card
                        title="Average return value"
                        data-intercom-target="average-return-value-analytics-customers"
                        bordered={true}
                        hoverable={false}
                        extra={<React.Fragment></React.Fragment>}
                      >
                        {this.state.averageReturnValue} {this.state.currency}
                      </Card>
                    </Tooltip>
                  </Col>
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 12 }}
                    md={{ span: 12 }}
                    lg={{ span: 8 }}
                  >
                    <Tooltip
                      placement="top"
                      title="Value of returned items that have been delivered or resolved. Applicable to return-status 'Received' and 'Resolved'."
                    >
                      <Card
                        title="Completed Return Value"
                        data-intercom-target="completed-return-value-analytics-customers"
                        bordered={true}
                        hoverable={false}
                        extra={<React.Fragment></React.Fragment>}
                      >
                        {this.state.valueOfCompletedReturns}{' '}
                        {this.state.currency}
                      </Card>
                    </Tooltip>
                  </Col>

                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 12 }}
                    md={{ span: 12 }}
                    lg={{ span: 8 }}
                  >
                    <Tooltip placement="top" title="Number of items returned.">
                      <Card
                        title="Returned Items"
                        data-intercom-target="returned-items-analytics-customers"
                        bordered={true}
                        hoverable={false}
                        extra={<React.Fragment></React.Fragment>}
                      >
                        {this.state.numberOfTotalProducts}
                      </Card>
                    </Tooltip>
                  </Col>
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 12 }}
                    md={{ span: 12 }}
                    lg={{ span: 8 }}
                  >
                    <Tooltip
                      placement="top"
                      title="Number of items returned divided by the number of returns."
                    >
                      <Card
                        title="Average items per return"
                        data-intercom-target="average-items-per-return-analytics-customers"
                        bordered={true}
                        hoverable={false}
                        extra={<React.Fragment></React.Fragment>}
                      >
                        {this.state.averageProductsPerReturn}
                      </Card>
                    </Tooltip>
                  </Col>
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 12 }}
                    md={{ span: 12 }}
                    lg={{ span: 8 }}
                  >
                    <Tooltip
                      placement="top"
                      title="Number of returns that have been delivered or resolved. Applicable to return-status 'Received' and 'Resolved'."
                    >
                      <Card
                        title="Completed Return Volume"
                        data-intercom-target="completed-return-volume-analytics-customers"
                        bordered={true}
                        hoverable={false}
                        extra={<React.Fragment></React.Fragment>}
                      >
                        {this.state.numberOfCompletedReturns}
                      </Card>
                    </Tooltip>
                  </Col>
                </Row>

                <div className="u-marginTop--lg">
                  <div className="TextHeading TextHeading--xs u-marginBottom--md">
                    Item based metrics
                  </div>
                </div>

                {/* <Divider style={{ marginTop: 20, marginBottom: 20 }}>
                  Item based Metrics
                </Divider> */}

                <Row
                  type="flex"
                  gutter={10}
                  style={{
                    marginTop: '20px',
                    textAlign: 'center',
                    rowGap: '15px',
                  }}
                >
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 12 }}
                    md={{ span: 12 }}
                    lg={{ span: 8 }}
                  >
                    <Tooltip
                      placement="top"
                      title="Number of items selected for Refund divided by number of items returned."
                    >
                      <Card
                        title="Refund Rate"
                        data-intercom-target="refund-rate-analytics-customers"
                        bordered={true}
                        hoverable={false}
                        extra={<React.Fragment></React.Fragment>}
                      >
                        {this.state.rateOfRefundToOriginal}
                      </Card>
                    </Tooltip>
                  </Col>
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 12 }}
                    md={{ span: 12 }}
                    lg={{ span: 8 }}
                  >
                    <Tooltip
                      placement="top"
                      title="Number of items selected for Refund."
                    >
                      <Card
                        title="Refund Volume"
                        data-intercom-target="refund-volume-analytics-customers"
                        bordered={true}
                        hoverable={false}
                        extra={<React.Fragment></React.Fragment>}
                      >
                        {this.state.numberOfRefundToOriginal}
                      </Card>
                    </Tooltip>
                  </Col>
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 12 }}
                    md={{ span: 12 }}
                    lg={{ span: 8 }}
                  >
                    <Tooltip
                      placement="top"
                      title="Value of items selected for Refund."
                    >
                      <Card
                        title="Refund Value"
                        data-intercom-target="refund-value-analytics-customers"
                        bordered={true}
                        hoverable={false}
                        extra={<React.Fragment></React.Fragment>}
                      >
                        {this.state.valueOfRefundToOriginal}{' '}
                        {this.state.currency}
                      </Card>
                    </Tooltip>
                  </Col>

                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 12 }}
                    md={{ span: 12 }}
                    lg={{ span: 8 }}
                  >
                    <Tooltip
                      placement="top"
                      title="Number of items selected for Exchange divided by number of items returned."
                    >
                      <Card
                        title="Exchange Rate"
                        data-intercom-target="exchange-rate-analytics-customers"
                        bordered={true}
                        hoverable={false}
                        extra={<React.Fragment></React.Fragment>}
                      >
                        {this.state.rateOfRefundToExchange}
                      </Card>
                    </Tooltip>
                  </Col>
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 12 }}
                    md={{ span: 12 }}
                    lg={{ span: 8 }}
                  >
                    <Tooltip
                      placement="top"
                      title="Number of items selected for Exchange."
                    >
                      <Card
                        title="Exchange Volume"
                        data-intercom-target="exchange-volume-analytics-customers"
                        bordered={true}
                        hoverable={false}
                        extra={<React.Fragment></React.Fragment>}
                      >
                        {this.state.numberOfRefundToExchange}
                      </Card>
                    </Tooltip>
                  </Col>
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 12 }}
                    md={{ span: 12 }}
                    lg={{ span: 8 }}
                  >
                    <Tooltip
                      placement="top"
                      title="Value of items selected for Exchange."
                    >
                      <Card
                        title="Exchange Value"
                        data-intercom-target="exchange-value-analytics-customers"
                        bordered={true}
                        hoverable={false}
                        extra={<React.Fragment></React.Fragment>}
                      >
                        {this.state.valueOfRefundToExchange}{' '}
                        {this.state.currency}
                      </Card>
                    </Tooltip>
                  </Col>

                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 12 }}
                    md={{ span: 12 }}
                    lg={{ span: 8 }}
                  >
                    <Tooltip
                      placement="top"
                      title="Number of items selected for Store-Credit divided by number of items returned."
                    >
                      <Card
                        title="Store Credit Rate"
                        data-intercom-target="store-credit-rate-analytics-customers"
                        bordered={true}
                        hoverable={false}
                        extra={<React.Fragment></React.Fragment>}
                      >
                        {this.state.rateOfRefundToCredit}
                      </Card>
                    </Tooltip>
                  </Col>
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 12 }}
                    md={{ span: 12 }}
                    lg={{ span: 8 }}
                  >
                    <Tooltip
                      placement="top"
                      title="Number of items selected for Store-Credit."
                    >
                      <Card
                        title="Store Credit Volume"
                        data-intercom-target="store-credit-volume-analytics-customers"
                        bordered={true}
                        hoverable={false}
                        extra={<React.Fragment></React.Fragment>}
                      >
                        {this.state.numberOfRefundToCredit}
                      </Card>
                    </Tooltip>
                  </Col>
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 12 }}
                    md={{ span: 12 }}
                    lg={{ span: 8 }}
                  >
                    <Tooltip
                      placement="top"
                      title="Value of items selected for Store-Credit."
                    >
                      <Card
                        title="Store Credit Value"
                        data-intercom-target="store-credit-value-analytics-customers"
                        bordered={true}
                        hoverable={false}
                        extra={<React.Fragment></React.Fragment>}
                      >
                        {this.state.valueOfRefundToCredit} {this.state.currency}
                      </Card>
                    </Tooltip>
                  </Col>
                </Row>
              </Spin>
            </div>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Row type="flex" justify="space-around" align="top">
              <Col span={24}>
                <Divider />
              </Col>
              <Upgrade
                title="Business Intelligence at your fingertips."
                description="Choose a plan with access to Reports. Explore insights into why customers return products and make smarter business decisions."
                videoId="VjbdXeknVMM"
              />
            </Row>
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }
}

export default Stats;
